/**
 * Writing or amending a sales document.
 *
 * Like the detail page it sits outside `SalesShell`, so it carries its own
 * `PageHead`. The form is three cards in a column - who and when, the lines,
 * the notes - rather than one long bordered box, and the fields are the CRM's
 * sunken pills instead of shadcn inputs. Nothing about what gets saved changed.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronLeft, Loader2, Save } from "lucide-react";
import { DatePicker } from "@wa/ui";
import { useI18n } from "../../../i18n/I18nProvider";
import { ApiError } from "../../../lib/api";
import { getClient } from "../../../lib/clients";
import { getNumbering } from "../../../lib/sales";
import type { CreditNote, Invoice, LineItem, NumberingConfig, Proposal, QtyMode } from "../../../lib/sales";
import { Btn, Card, PageHead, useToast } from "../../ui";
import { DOC_CONFIG, KIND_I18N, type AnyDoc, type DocKind } from "./config";
import { defaultDeadline } from "./docDates";
import { ClientPicker, LineItemsEditor, ProjectPicker } from "./shared";

/**
 * The form's id, so the Save and Cancel buttons can sit in the page head where
 * the design puts a screen's actions while still belonging to the form below
 * them. A second pair at the foot would be two ways to do one thing.
 */
const FORM_ID = "sales-doc-form";

function isoToDate(s?: string | null): string {
  return s ? s.slice(0, 10) : "";
}
function dateToIso(s: string): string | undefined {
  return s ? new Date(s + "T00:00:00").toISOString() : undefined;
}
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function DocEditor({ kind }: { kind: DocKind }) {
  const { t, locale } = useI18n();
  const toast = useToast();
  const cfg = DOC_CONFIG[kind];
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const isNew = !id || id === "new";

  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  /**
   * A new document can arrive pre-addressed. The project page's "New" menu links
   * here with `?clientId=&projectId=`, so an operator who is already looking at a
   * project does not have to find that project again in two pickers - and, more
   * to the point, cannot pick the wrong one while copying it across by eye.
   *
   * Read ONCE, as the initial state, and never in an effect. An effect watching
   * the query string would also fire on the render after the operator changed the
   * client here, and would quietly put the link's answer back over theirs.
   *
   * The pair cannot drift apart afterwards: the client picker below clears
   * `projectId` whenever the client changes, which is the same rule the server
   * enforces by refusing a cross-client link with `project_client_mismatch`. So a
   * pre-filled project either belongs to the pre-filled client or is gone.
   */
  const [search] = useSearchParams();
  const [clientId, setClientId] = useState(() => (isNew ? (search.get("clientId") ?? "") : ""));
  /** The linked project's id, or "" for none. Only estimates and invoices have
   *  the column - see `hasProject` in config.ts. */
  const [projectId, setProjectId] = useState(() =>
    isNew && cfg.hasProject ? (search.get("projectId") ?? "") : "",
  );
  const [title, setTitle] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  /** The invoice date (YYYY-MM-DD). */
  const [issueDateVal, setIssueDateVal] = useState(() => (isNew && cfg.hasIssueDate ? todayIso() : ""));
  /** Either validUntil OR dueDate (YYYY-MM-DD) - no kind has both. */
  const [dateVal, setDateVal] = useState("");
  /**
   * True once the operator has set the deadline themselves. Until then the field
   * follows the configured term, and after it nothing may move it: a date the
   * operator typed is an instruction, not a starting point.
   */
  const [dateTouched, setDateTouched] = useState(false);
  /** The invoicing settings, once they answer. Null means "not asked yet", which
   *  is why the field is left alone rather than emptied while it loads. */
  const [nums, setNums] = useState<NumberingConfig | null>(null);
  /** The picked client's own payment term, when they have one. Invoices only. */
  const [clientTerm, setClientTerm] = useState<number | null>(null);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [qtyMode, setQtyMode] = useState<QtyMode>("qty");

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    cfg.api
      .get(id!)
      .then((d) => {
        setClientId(d.clientId);
        setLineItems(d.lineItems ?? []);
        setQtyMode(d.qtyMode ?? "qty");
        setNotes(d.notes ?? "");
        if (cfg.hasProject) setProjectId((d as Invoice).projectId ?? "");
        if (cfg.hasTitle) setTitle((d as Proposal).title ?? "");
        if (kind === "credit-notes") setReason((d as CreditNote).reason ?? "");
        if (cfg.hasIssueDate) setIssueDateVal(isoToDate((d as Invoice).issueDate));
        if (cfg.hasValidUntil) setDateVal(isoToDate((d as Proposal).validUntil));
        if (cfg.hasDueDate) setDateVal(isoToDate((d as Invoice).dueDate));
      })
      .finally(() => setLoading(false));
  }, [cfg, id, isNew, kind]);

  /**
   * The configured payment term, for a new document that has a deadline field.
   *
   * Only for a new one: an existing document already carries the date it was
   * written with, and a term that has been changed in Settings since must not
   * silently re-date it while somebody edits its notes.
   */
  useEffect(() => {
    if (!isNew || !(cfg.hasDueDate || cfg.hasValidUntil)) return;
    let alive = true;
    getNumbering()
      .then((n) => {
        if (alive) setNums(n);
      })
      .catch(() => {
        // A settings read that fails leaves `nums` null, which shows an empty
        // field. That is the behaviour this whole block replaced, so a failure
        // here costs the convenience and nothing else.
      });
    return () => {
      alive = false;
    };
  }, [cfg, isNew]);

  /**
   * The picked client's own payment term, which beats the global one on an
   * invoice. Read here rather than taken from the picker: the picker holds the
   * first hundred clients, and the hundred-and-first must not quietly fall back
   * to the general term.
   */
  useEffect(() => {
    if (!isNew || !cfg.hasDueDate) return;
    if (!clientId) {
      setClientTerm(null);
      return;
    }
    let alive = true;
    getClient(clientId)
      .then((c) => {
        if (alive) setClientTerm(c.paymentTermsDays ?? null);
      })
      .catch(() => {
        if (alive) setClientTerm(null);
      });
    return () => {
      alive = false;
    };
  }, [cfg, clientId, isNew]);

  /**
   * Offer the deadline the settings imply, until the operator sets one.
   *
   * Re-runs whenever anything it is derived from moves - the settings arriving,
   * a client being picked, the invoice date being changed - so the field always
   * shows the date that would be written, rather than an answer computed once
   * before the client was known.
   */
  useEffect(() => {
    if (!isNew || dateTouched) return;
    setDateVal(defaultDeadline({ kind, cfg: nums, clientTermDays: clientTerm, from: issueDateVal }));
  }, [clientTerm, dateTouched, isNew, issueDateVal, kind, nums]);

  const incomplete = !clientId || lineItems.length === 0;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (incomplete) return;
    setBusy(true);
    try {
      const body: Record<string, unknown> = { clientId, lineItems, qtyMode, notes: notes || undefined };
      // Explicitly null rather than omitted when empty: both routes take
      // `projectId` as nullable, and omitting the key on a PATCH means "leave
      // the link alone", which would make unlinking impossible.
      if (cfg.hasProject) body.projectId = projectId || null;
      if (cfg.hasTitle) body.title = title || undefined;
      if (kind === "credit-notes") body.reason = reason || undefined;
      if (cfg.hasIssueDate) {
        const iso = dateToIso(issueDateVal);
        if (iso) body.issueDate = iso;
      }
      // Three states, not two. A filled field sends its date. An empty field
      // the operator emptied THEMSELVES sends null, which is how both routes
      // are told there is no deadline - otherwise the server would put the
      // configured term back and the detail page would show a date that was
      // just deleted. An empty field nobody touched is not a decision (the
      // settings read can fail), so the key is omitted and the server applies
      // the same term this form would have shown.
      //
      // Until this existed, a create always sent null, which the routes refused
      // with a 400 the operator only saw as a bare "something went wrong".
      const deadline = dateToIso(dateVal) ?? (isNew && !dateTouched ? undefined : null);
      if (cfg.hasValidUntil) body.validUntil = deadline;
      if (cfg.hasDueDate) body.dueDate = deadline;

      const saved: AnyDoc = isNew ? await cfg.api.create(body) : await cfg.api.update(id!, body);
      nav(`/sales/${kind}/${saved.id}`);
    } catch (e) {
      // The project link is the one field whose refusal has a name. There is no
      // foreign key behind `projectId`, so the routes check by hand that the
      // project belongs to this client and answer `400 project_client_mismatch`
      // when it does not - and a save that failed for THAT reason has to say so,
      // because the operator is looking at a field that appears to be filled in
      // correctly. Everything else keeps the generic line: until this catch
      // existed a failed save simply returned the button to its resting state
      // and left the screen claiming nothing had happened.
      const mismatch = e instanceof ApiError && (e.body as { error?: string } | null)?.error === "project_client_mismatch";
      toast(t(mismatch ? "sales.projectMismatch" : "common.error"));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16 text-muted-foreground">
        <Loader2 className="size-6 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <Link to={`/sales/${kind}`} className="crm-btn crm-btn-sm mb-4">
        <ChevronLeft className="size-4" />
        {t("sales.back")}
      </Link>

      <PageHead
        title={t(`sales.${KIND_I18N[kind]}.${isNew ? "new" : "edit"}`)}
        sub={t("sales.editorIntro")}
        actions={
          <>
            {/* A link, not a button: cancelling is navigation, and a link is
                the only version of it the keyboard and the middle mouse button
                both understand. */}
            <Link to={`/sales/${kind}`} className="crm-btn">
              {t("sales.cancel")}
            </Link>
            {/* Outside the <form> but owned by it through `form`, which is what
                lets the save live in the design's action slot. */}
            <Btn type="submit" form={FORM_ID} tone="dark" disabled={busy || incomplete}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
              {t("sales.save")}
            </Btn>
          </>
        }
      />

      <form id={FORM_ID} onSubmit={submit}>
        <Card className="px-[26px] pb-[26px] pt-1">
          <div className="grid gap-x-5 sm:grid-cols-2">
            <div>
              <label className="crm-lab">{t("sales.client")} *</label>
              <ClientPicker
                value={clientId}
                onChange={(next) => {
                  // A project belongs to exactly one client, so moving the
                  // document to another client invalidates whatever was linked.
                  // Cleared here rather than left for the server to refuse: the
                  // picker below would otherwise still show the old project's
                  // name, which is a field that looks right and saves wrong.
                  if (next !== clientId) setProjectId("");
                  setClientId(next);
                }}
              />
            </div>

            {cfg.hasProject ? (
              <div>
                <label className="crm-lab">{t("sales.project")}</label>
                <ProjectPicker clientId={clientId} value={projectId} onChange={setProjectId} />
              </div>
            ) : null}

            {cfg.hasIssueDate ? (
              <div>
                <label className="crm-lab">{t("sales.issueDateLabel")}</label>
                <DatePicker value={issueDateVal} onChange={setIssueDateVal} locale={locale} />
              </div>
            ) : null}

            {/* Title on the left, the deadline beside it on the same row. */}
            {cfg.hasTitle ? (
              <div>
                <label className="crm-lab">{t("sales.docTitle")}</label>
                <label className="crm-inp">
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t("sales.docTitlePlaceholder")}
                    autoComplete="off"
                  />
                </label>
              </div>
            ) : null}

            {cfg.hasValidUntil || cfg.hasDueDate ? (
              <div>
                <label className="crm-lab">{cfg.hasDueDate ? t("sales.dueDate") : t("sales.validUntil")}</label>
                {/* Every route into this field is the operator's own: the
                    picker fires on a day and on the clear button, and never on
                    mount. So marking it touched here is enough to stop the
                    default from coming back over their answer - including when
                    they deliberately empty it. */}
                <DatePicker
                  value={dateVal}
                  onChange={(v) => {
                    setDateTouched(true);
                    setDateVal(v);
                  }}
                  locale={locale}
                />
              </div>
            ) : null}
          </div>

          {kind === "credit-notes" ? (
            <div>
              <label className="crm-lab">{t("sales.reason")}</label>
              <label className="crm-inp">
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t("sales.reasonPlaceholder")}
                  autoComplete="off"
                />
              </label>
            </div>
          ) : null}
        </Card>

        {/* The line editor brings its own card, so it sits on the page rather
            than inside one - a card within a card reads as a nested document. */}
        <h2 className="crm-card-t mb-3 mt-6 px-1.5">{t("sales.lines")}</h2>
        <LineItemsEditor value={lineItems} onChange={setLineItems} qtyMode={qtyMode} onQtyModeChange={setQtyMode} />

        <Card className="mt-6 px-[26px] pb-[26px] pt-1">
          <label className="crm-lab">{t("sales.notes")}</label>
          <textarea
            className="crm-area"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("sales.notesPlaceholder")}
          />
        </Card>
      </form>
    </div>
  );
}
